import { useQuery } from '@tanstack/react-query';
import {
  apartmentAllChannelEventsQuery,
  apartmentChannelEventsQuery,
  hasProviderSyncErrors,
  useAcknowledgeChannelEvents,
  type ChannelSyncEvent,
  type Provider,
} from '@/lib/channels';
import { usePermissions } from '@/lib/me';
import { useToast } from '@/components/ToastProvider';
import { errorMessage } from '@/lib/errors';
import { relativeDateTime } from '@/lib/format';
import { Button } from '@/components/ui/button';

// SyncErrors — the Channels-tab sync-error surface (Task 11 + Task 14). Fixes
// the bug this task exists for: ChannelCard used to count every error row
// ever recorded with no time window and no way to dismiss one, so a 15-day-old
// error read as "1 recent error" and could hide a brand-new one behind it.
//
// `inactive_reason` is carried straight through from the API — it is
// computed server-side by the channel_sync_event_state view, not re-derived
// here.
const REASON_LABEL: Record<string, string> = {
  acknowledged: 'acknowledged',
  manual_sync: 'resolved by manual sync',
  outbox_push: 'pushed again, no new errors since',
};

// A row with no `inactive_reason` is never a cleared error: the view defines
// `active = (severity = 'error' AND inactive_reason IS NULL)`, so an
// inactive ERROR always carries a reason. The reasonless rows reaching the
// history list are therefore warnings, which were never active and must not
// be labelled "resolved" — that was a lie an operator could act on.
function historyLabel(e: ChannelSyncEvent): string {
  if (e.inactive_reason) return REASON_LABEL[e.inactive_reason] ?? e.inactive_reason;
  return e.severity === 'error' ? 'resolved' : e.severity;
}

function byOccurredDesc(a: ChannelSyncEvent, b: ChannelSyncEvent): number {
  return new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime();
}

// useAcknowledgeHandler wraps an acknowledge mutation with the shared
// success/failure toast — identical in both ProviderSyncErrors and
// OtherSyncErrors, so it lives once here instead of twice.
function useAcknowledgeHandler(ack: ReturnType<typeof useAcknowledgeChannelEvents>) {
  const { toast } = useToast();
  return async (ids: string[]) => {
    try {
      await ack.mutateAsync(ids);
      toast({
        kind: 'success',
        title: ids.length > 1 ? 'Errors acknowledged' : 'Error acknowledged',
      });
    } catch (err) {
      toast({ kind: 'error', title: 'Could not acknowledge', message: errorMessage(err) });
    }
  };
}

function FetchErrorNotice({ error }: { error: unknown }) {
  return (
    <p
      role="alert"
      className="mt-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-xs text-destructive"
    >
      {errorMessage(error)}
    </p>
  );
}

// ActiveHistoryRows renders the shared active/history list body — the row
// shape both the per-provider card and the apartment-wide "Other sync
// errors" block use: relative age, error_type from payload, an Acknowledge
// button per active row and "Acknowledge all" for the block, all gated on
// `canWrite`, plus a collapsed History list annotated from inactive_reason.
function ActiveHistoryRows({
  active,
  history,
  canWrite,
  acknowledging,
  onAcknowledge,
}: {
  active: ChannelSyncEvent[];
  history: ChannelSyncEvent[];
  canWrite: boolean;
  acknowledging: boolean;
  onAcknowledge: (ids: string[]) => void;
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        {/* No count here — `active.length` is a client filter over this
            query's own (capped) page, and would disagree with ChannelCard's
            server-sourced active_error_count and the Channels tab's
            active_error_total once a provider passes the page limit. The
            rendered rows below are the count; render them, not a number. */}
        <h4 className="text-xs font-semibold text-foreground">Active errors</h4>
        {canWrite && active.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={acknowledging}
            onClick={() => onAcknowledge(active.map((e) => e.id))}
          >
            Acknowledge all
          </Button>
        )}
      </div>

      {active.length > 0 && (
        <ul className="space-y-2">
          {active.map((e) => (
            <li
              key={e.id}
              className="flex items-start justify-between gap-2 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-xs"
            >
              <p className="min-w-0 break-words text-destructive">
                {relativeDateTime(e.occurred_at)}
                {typeof e.payload.error_type === 'string' && (
                  <>
                    {' · '}
                    <code className="break-all">{e.payload.error_type}</code>
                  </>
                )}
              </p>
              {canWrite && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-none"
                  loading={acknowledging}
                  onClick={() => onAcknowledge([e.id])}
                >
                  Acknowledge
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {history.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted">History ({history.length})</summary>
          <ul className="mt-2 space-y-1">
            {history.map((e) => (
              <li key={e.id} className="text-muted">
                {relativeDateTime(e.occurred_at)} — {historyLabel(e)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

/**
 * `provider` omitted renders the apartment-wide "Other sync errors" block
 * instead of a per-provider active/history list — see {@link OtherSyncErrors}.
 */
export function SyncErrors({
  apartmentID,
  provider,
}: {
  apartmentID: string;
  provider?: Provider;
}) {
  if (!provider) return <OtherSyncErrors apartmentID={apartmentID} />;
  return <ProviderSyncErrors apartmentID={apartmentID} provider={provider} />;
}

// OtherSyncErrors — active/history channel_sync_events rows that no provider
// card above accounts for. Two kinds reach it, and neither has another path:
//
//   * unattributed rows — the channel manager's payload couldn't be resolved
//     to a known channel, so `provider` is NULL and no per-provider GET
//     returns them (this apartment-wide GET is the only one that does);
//   * rows attributed to a provider whose card carries no sync-error list
//     (`hasProviderSyncErrors` is false). The DB's CHECK permits
//     `booking_engine` here even though the live feed never produces it, so
//     without this arm such a row would be stored, counted in
//     `active_error_total`, and invisible everywhere.
//
// Rows belonging to a card above are deliberately excluded — they already
// appear there, and showing them twice would double the operator's work.
//
// Renders nothing when there is no residual at all: no empty block, no
// "0 other errors" line.
function OtherSyncErrors({ apartmentID }: { apartmentID: string }) {
  const { data: events = [], error } = useQuery(apartmentAllChannelEventsQuery(apartmentID));
  const { can } = usePermissions();
  const canWrite = can('apartments.write');
  const ack = useAcknowledgeChannelEvents(apartmentID);
  const acknowledge = useAcknowledgeHandler(ack);

  if (error) return <FetchErrorNotice error={error} />;

  const residual = events.filter((e) => !hasProviderSyncErrors(e.provider));
  if (residual.length === 0) return null;

  const active = residual.filter((e) => e.active).sort(byOccurredDesc);
  const history = residual.filter((e) => !e.active).sort(byOccurredDesc);

  return (
    <section className="mt-4 space-y-2 rounded-[var(--radius-control)] border border-[var(--border-soft)] bg-[var(--surface-sunken)] p-4">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        Other sync errors
      </h3>
      <ActiveHistoryRows
        active={active}
        history={history}
        canWrite={canWrite}
        acknowledging={ack.isPending}
        onAcknowledge={(ids) => void acknowledge(ids)}
      />
    </section>
  );
}

function ProviderSyncErrors({
  apartmentID,
  provider,
}: {
  apartmentID: string;
  provider: Provider;
}) {
  const { data: events = [], error } = useQuery(apartmentChannelEventsQuery(apartmentID, provider));
  const { can } = usePermissions();
  const canWrite = can('apartments.write');
  const ack = useAcknowledgeChannelEvents(apartmentID, provider);
  const acknowledge = useAcknowledgeHandler(ack);

  if (error) return <FetchErrorNotice error={error} />;

  // A provider that has never recorded a sync error shows nothing extra —
  // no permanent empty "Active errors" line cluttering every card. Once
  // there's at least one row (active or resolved), the block renders so the
  // operator can see the history even after everything's been cleared.
  if (events.length === 0) return null;

  const active = events.filter((e) => e.active).sort(byOccurredDesc);
  const history = events.filter((e) => !e.active).sort(byOccurredDesc);

  return (
    <div className="mb-3 mt-3 space-y-2 border-t border-[var(--border-soft)] pt-3">
      <ActiveHistoryRows
        active={active}
        history={history}
        canWrite={canWrite}
        acknowledging={ack.isPending}
        onAcknowledge={(ids) => void acknowledge(ids)}
      />
    </div>
  );
}
